import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useComboboxTypeahead } from "@/hooks/useComboboxTypeahead";
import {
  fetchChartOfAccountsTree,
  listLeafAccounts,
  type ChartOfAccountNode,
} from "@/lib/dataBank/chartOfAccountsStore";
import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown, ListTree, Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

type ChartOfAccountLeafSelectProps = {
  value: string;
  onChange: (accountId: string, account?: ChartOfAccountNode | null) => void;
  placeholder?: string;
  triggerClassName?: string;
  disabled?: boolean;
  /** When true, shows an option that clears the selection (empty value). */
  allowClear?: boolean;
  clearLabel?: string;
};

function itemValue(id: number): string {
  return `coa:${id}`;
}

export function ChartOfAccountLeafSelect({
  value,
  onChange,
  placeholder = "Select account",
  triggerClassName,
  disabled = false,
  allowClear = false,
  clearLabel = "Clear selection",
}: ChartOfAccountLeafSelectProps) {
  const { open, setOpen, onTriggerKeyDown, onOpenChange, commandInputProps } =
    useComboboxTypeahead(disabled);
  const [highlight, setHighlight] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [leaves, setLeaves] = useState<ChartOfAccountNode[]>([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(false);
    void fetchChartOfAccountsTree()
      .then((tree) => {
        if (!cancelled) setLeaves(listLeafAccounts(tree));
      })
      .catch(() => {
        if (!cancelled) {
          setLeaves([]);
          setLoadError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const selected = useMemo(
    () => leaves.find((node) => String(node.accountId) === value.trim()) ?? null,
    [leaves, value],
  );

  const groups = useMemo(() => {
    const byClass = new Map<string, ChartOfAccountNode[]>();
    for (const node of leaves) {
      const key = node.accountClass || "Accounts";
      const list = byClass.get(key) ?? [];
      list.push(node);
      byClass.set(key, list);
    }
    return Array.from(byClass.entries()).map(([parent, items]) => ({
      parent,
      items: [...items].sort(
        (a, b) =>
          a.code.localeCompare(b.code, undefined, { numeric: true }) ||
          a.name.localeCompare(b.name, undefined, { numeric: true }),
      ),
    }));
  }, [leaves]);

  useEffect(() => {
    if (!open) return;
    setHighlight(value ? itemValue(Number(value)) : "");
    const frame = requestAnimationFrame(() => {
      document
        .querySelector<HTMLElement>("[data-coa-leaf-selected='true']")
        ?.scrollIntoView({ block: "nearest" });
    });
    return () => cancelAnimationFrame(frame);
  }, [open, value]);

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (next) setHighlight(value ? itemValue(Number(value)) : "");
      }}
      modal
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled || loading}
          onKeyDown={onTriggerKeyDown}
          className={cn(
            "glass-input h-11 w-full justify-between px-3.5 font-normal text-sm transition-all",
            "hover:!bg-muted/30 hover:border-primary/40",
            selected
              ? "text-foreground hover:!text-foreground"
              : "text-muted-foreground hover:!text-muted-foreground",
            triggerClassName,
          )}
        >
          <span className="flex min-w-0 items-center gap-2 truncate">
            {loading ? (
              <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin opacity-60 text-primary" />
            ) : (
              <ListTree className="h-3.5 w-3.5 shrink-0 opacity-60 text-primary" />
            )}
            <span className="truncate">
              {loading
                ? "Loading accounts…"
                : loadError
                  ? "Could not load accounts"
                  : selected ? (
                    <>
                      <span className="font-semibold text-foreground">{selected.name}</span>
                      {selected.code ? (
                        <span className="ml-1.5 text-xs font-normal text-muted-foreground">
                          ({selected.code})
                        </span>
                      ) : null}
                    </>
                  ) : (
                    <span className="text-muted-foreground hover:text-muted-foreground">{placeholder}</span>
                  )}
            </span>
          </span>
          <ChevronsUpDown className="ml-1.5 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] min-w-[300px] p-0 z-[100] shadow-xl border-border/60"
        align="start"
        sideOffset={4}
      >
        <Command
          value={highlight}
          onValueChange={setHighlight}
          filter={(item, search, keywords) => {
            const q = search.trim().toLowerCase();
            if (!q) return 1;
            const haystack = `${item} ${(keywords ?? []).join(" ")}`.toLowerCase();
            return haystack.includes(q) ? 1 : 0;
          }}
        >
          <CommandInput
            {...commandInputProps}
            placeholder="Search account, code or group…"
            className="h-10 text-xs border-none focus:ring-0"
          />
          <CommandList className="max-h-[260px] overflow-y-auto p-1">
            <CommandEmpty className="py-3 text-center text-xs text-muted-foreground">
              {loading
                ? "Loading accounts…"
                : loadError
                  ? "Could not load accounts."
                  : leaves.length === 0
                    ? "No leaf accounts found."
                    : "No account found."}
            </CommandEmpty>
            {allowClear ? (
              <CommandGroup>
                <CommandItem
                  value="coa:clear"
                  keywords={["clear", "all", "daily", "vouchers"]}
                  onSelect={() => {
                    onChange("", null);
                    setOpen(false);
                  }}
                  className="text-xs cursor-pointer rounded-md px-2.5 py-2 my-0.5"
                >
                  <span className="truncate text-muted-foreground">{clearLabel}</span>
                  <Check
                    className={cn(
                      "ml-2 h-3.5 w-3.5 shrink-0 text-primary",
                      !value.trim() ? "opacity-100" : "opacity-0",
                    )}
                  />
                </CommandItem>
              </CommandGroup>
            ) : null}
            {groups.map((group) => (
              <CommandGroup
                key={group.parent}
                heading={group.parent}
                className="[&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-bold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-primary/80 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5"
              >
                {group.items.map((node) => {
                  const id = node.accountId!;
                  const val = itemValue(id);
                  const isSelected = value === String(id);
                  return (
                    <CommandItem
                      key={id}
                      value={val}
                      keywords={[node.code, node.name, node.accountGroup ?? "", node.accountClass]}
                      data-coa-leaf-selected={isSelected ? "true" : undefined}
                      onSelect={() => {
                        onChange(String(id), node);
                        setOpen(false);
                      }}
                      className={cn(
                        "text-xs flex items-center justify-between cursor-pointer rounded-md px-2.5 py-2 my-0.5 transition-colors",
                        isSelected && "bg-primary/10 font-semibold text-primary",
                      )}
                    >
                      <div className="flex min-w-0 items-center gap-2 pr-2">
                        <span className="truncate font-medium">{node.name}</span>
                        {node.code ? (
                          <span className="shrink-0 rounded border border-border/30 bg-muted/60 px-1.5 py-0.5 font-mono text-[10px] opacity-70">
                            #{node.code}
                          </span>
                        ) : null}
                      </div>
                      <Check
                        className={cn(
                          "ml-2 h-3.5 w-3.5 shrink-0 text-primary",
                          isSelected ? "opacity-100" : "opacity-0",
                        )}
                      />
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
